// PassPreviewPanel — wraps the shared PassPreview with the builder's layout
// behaviour (slice 4a-3): a sticky right-side panel on desktop, a collapsible
// bottom sheet on mobile. The program data is debounced 200ms upstream so the
// memoized PassPreview only repaints when edits settle.

import { useEffect, useState } from 'react'

import {
  PassPreview,
  type PassPreviewProgram,
} from '../../shared/pass-preview/PassPreview.js'
import './passPreviewPanel.css'

export function PassPreviewPanel({
  program,
  variant,
  desiredSide,
}: {
  program: PassPreviewProgram
  /** `side` = desktop sticky panel; `sheet` = mobile fullscreen overlay. */
  variant: 'side' | 'sheet'
  /** Forwarded to PassPreview — follows the builder's current step. The
   *  sign-up step opens the Enroll preview, back-of-pass step opens the
   *  back, everything else opens the front. The owner can still toggle. */
  desiredSide?: 'front' | 'back' | 'enroll'
}) {
  const [open, setOpen] = useState(false)

  // Lock body scroll while the overlay is open so the page underneath doesn't
  // jitter under finger drags.
  useEffect(() => {
    if (variant !== 'sheet' || !open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [variant, open])

  if (variant === 'side') {
    return (
      <aside className="program-preview-side" aria-label="Pass preview">
        <span className="program-preview-side-label">Instant preview</span>
        <PassPreview
          program={program}
          size="compact"
          desiredSide={desiredSide}
        />
        <PreviewFootnote />
      </aside>
    )
  }

  // Mobile — a sticky bottom handle opens a fullscreen overlay.
  return (
    <>
      <div className="program-preview-trigger">
        <button
          type="button"
          className="program-preview-handle"
          onClick={() => setOpen(true)}
          aria-expanded={open}
        >
          Instant preview
        </button>
      </div>

      {open && (
        <div
          className="program-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Pass preview"
        >
          <div className="program-preview-overlay-bar">
            <span className="program-preview-overlay-title">
              Instant preview
            </span>
            <button
              type="button"
              className="program-preview-overlay-close"
              onClick={() => setOpen(false)}
              aria-label="Close preview"
            >
              Close
            </button>
          </div>
          <div className="program-preview-overlay-body">
            <PassPreview
              program={program}
              size="large"
              desiredSide={desiredSide}
            />
            <PreviewFootnote />
          </div>
        </div>
      )}
    </>
  )
}

function PreviewFootnote() {
  return (
    <p className="program-preview-footnote">
      Apple and Google passes use their own typography and layout — the live
      pass on a customer's phone won't look pixel-identical to this preview,
      but the colors, stamps, fields, and rewards all carry over.
    </p>
  )
}
